/**
 * PIECalibrationReviewPage — super-admin only. Audits whether the
 * engine's predicted ROE matches the realised ROE on validated
 * commitments, broken down by archetype.
 *
 * Three layers:
 *   1. Global verdict pill — is the engine well-calibrated overall?
 *   2. Per-archetype rows — count, avg predicted, avg realised, avg
 *      diff in pp, and a verdict badge per row.
 *   3. Verdict bucket distribution per row — how many calls landed in
 *      each calibration bucket so you can see whether a row is right
 *      ON AVERAGE but wrong on most CALLS.
 */
import React, { useEffect, useState } from 'react';
import { Scale, AlertCircle, TrendingUp, TrendingDown, Equal, ShieldCheck } from 'lucide-react';
import PIELayout from './PIELayout';
import { PIEPageSkeleton } from './PIESkeleton';
import { pieApi } from './pieApi';
import logger from '../../lib/logger';
import './pie.css';

const VERDICT_STYLES = {
  optimistic: {
    label: 'Engine ran conservative',
    color: '#0E7490', bg: 'rgba(6, 182, 212, 0.16)',
    icon: TrendingUp,
  },
  well_calibrated: {
    label: 'Well-calibrated',
    color: '#047857', bg: 'rgba(16, 185, 129, 0.16)',
    icon: Equal,
  },
  pessimistic: {
    label: 'Engine ran optimistic',
    color: '#B91C1C', bg: 'rgba(239, 68, 68, 0.16)',
    icon: TrendingDown,
  },
};

export default function PIECalibrationReviewPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    pieApi.calibrationReview()
      .then(setData)
      .catch((e) => {
        logger.error('calibration review failed', e);
        setErr(e?.response?.data?.detail || 'Could not load calibration review.');
      });
  }, []);

  if (!data && !err) {
    return (
      <PIELayout>
        <PIEPageSkeleton cards={3} />
      </PIELayout>
    );
  }
  if (err) {
    return (
      <PIELayout>
        <div className="pie-error" data-testid="pie-calibration-error">
          <AlertCircle size={14} aria-hidden style={{ verticalAlign: -2, marginRight: 6 }} />
          {err}
        </div>
      </PIELayout>
    );
  }

  return (
    <PIELayout>
      <div className="pie-canvas" data-testid="pie-calibration-page">
        <section className="pie-hero-center" style={{ marginBottom: 24 }}>
          <span className="pie-eyebrow"><Scale size={12} /> Calibration review · super admin</span>
          <h1 className="pie-hero-h1" style={{ marginBottom: 12 }}>
            Are our <span className="pie-accent-text">predictions honest?</span>
          </h1>
          <p className="pie-hero-sub">
            Every validated commitment lets us compare what the engine forecast against what
            the user actually realised. The bands below tell you whether the math is
            well-calibrated, optimistic, or pessimistic — globally and per archetype.
          </p>
        </section>

        <GlobalSummary g={data.global} band={data.thresholds?.well_calibrated_band_pp || 5} />

        {!data.rows || data.rows.length === 0 ? (
          <EmptyState />
        ) : (
          <ArchetypeTable rows={data.rows} />
        )}
      </div>
    </PIELayout>
  );
}

function GlobalSummary({ g, band }) {
  const v = VERDICT_STYLES[g.verdict] || VERDICT_STYLES.well_calibrated;
  const Icon = v.icon;
  return (
    <section
      className="pie-card"
      style={{ marginBottom: 18, padding: '18px 22px' }}
      data-testid="pie-calibration-global"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 12 }}>
        <h2 className="pie-display-md" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={16} aria-hidden style={{ color: 'var(--pie-accent)' }} />
          Engine-wide verdict
        </h2>
        <div className="pie-mono" style={{ fontSize: 12, color: 'var(--pie-text-secondary)' }}>
          {g.count} validated commitment{g.count === 1 ? '' : 's'} · ±{band}pp band
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 14, flexWrap: 'wrap' }}>
        <div
          data-testid="pie-calibration-global-verdict-badge"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: v.bg, color: v.color,
            padding: '8px 14px', borderRadius: 999,
            fontWeight: 700, fontSize: 13, letterSpacing: 0.2,
          }}
        >
          <Icon size={14} aria-hidden /> {v.label}
        </div>
        <div className="pie-mono" style={{ fontSize: 14, color: 'var(--pie-text)' }}>
          Avg delta:{' '}
          <strong>
            {g.avg_diff_pp >= 0 ? '+' : ''}{g.avg_diff_pp}pp
          </strong>
        </div>
      </div>
      <p style={{ fontSize: 12, color: 'var(--pie-text-secondary)', marginTop: 10, lineHeight: 1.55 }}>
        {g.verdict_explainer}
      </p>
      <BucketStrip buckets={g.verdict_buckets} count={g.count} testid="pie-calibration-global-buckets" />
    </section>
  );
}

function ArchetypeTable({ rows }) {
  return (
    <section className="pie-card" style={{ marginBottom: 18 }} data-testid="pie-calibration-archetype-card">
      <h2 className="pie-display-md" style={{ marginBottom: 6 }}>By archetype</h2>
      <p style={{ fontSize: 13, color: 'var(--pie-text-secondary)', marginBottom: 14, lineHeight: 1.55 }}>
        If one archetype consistently lands outside the ±5pp band, that&apos;s where the
        engine&apos;s assumptions need re-tuning — not where the users need re-training.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table
          className="pie-calibration-table"
          data-testid="pie-calibration-archetype-table"
          style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px', minWidth: 760 }}
        >
          <thead>
            <tr>
              {['Archetype', 'Count', 'Avg predicted', 'Avg realised', 'Δ pp', 'Verdict', 'Bucket distribution'].map((h) => (
                <th
                  key={h}
                  scope="col"
                  style={{
                    textAlign: 'left', fontSize: 11, color: 'var(--pie-text-muted)',
                    fontWeight: 600, padding: '4px 10px', whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <ArchetypeRow key={r.archetype} row={r} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ArchetypeRow({ row }) {
  const v = VERDICT_STYLES[row.verdict] || VERDICT_STYLES.well_calibrated;
  const Icon = v.icon;
  return (
    <tr data-testid={`pie-calibration-row-${row.archetype}`}>
      <td style={{ padding: '8px 10px', verticalAlign: 'middle', fontSize: 13, fontWeight: 600, color: 'var(--pie-text)' }}>
        {row.archetype_label}
      </td>
      <td style={{ padding: '8px 10px', verticalAlign: 'middle', fontSize: 13, color: 'var(--pie-text-secondary)' }} className="pie-mono">
        {row.count}
      </td>
      <td style={{ padding: '8px 10px', verticalAlign: 'middle', fontSize: 13, color: 'var(--pie-text-secondary)' }} className="pie-mono">
        {row.avg_predicted_roe_pct}%
      </td>
      <td style={{ padding: '8px 10px', verticalAlign: 'middle', fontSize: 13, color: 'var(--pie-text-secondary)' }} className="pie-mono">
        {row.avg_realised_roe_pct}%
      </td>
      <td style={{ padding: '8px 10px', verticalAlign: 'middle', fontSize: 13, color: 'var(--pie-text)', fontWeight: 600 }} className="pie-mono">
        {row.avg_diff_pp >= 0 ? '+' : ''}{row.avg_diff_pp}
      </td>
      <td style={{ padding: '8px 10px', verticalAlign: 'middle' }}>
        <span
          data-testid={`pie-calibration-row-verdict-${row.archetype}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: v.bg, color: v.color,
            padding: '4px 10px', borderRadius: 999,
            fontWeight: 600, fontSize: 11, letterSpacing: 0.2,
          }}
        >
          <Icon size={11} aria-hidden /> {v.label}
        </span>
      </td>
      <td style={{ padding: '8px 10px', verticalAlign: 'middle', minWidth: 200 }}>
        <BucketStrip buckets={row.verdict_buckets} count={row.count} testid={`pie-calibration-row-buckets-${row.archetype}`} compact />
      </td>
    </tr>
  );
}

function BucketStrip({ buckets, count, testid, compact }) {
  if (!count) return null;
  const order = ['pessimistic', 'well_calibrated', 'optimistic'];
  return (
    <div
      data-testid={testid}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, marginTop: compact ? 0 : 12,
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          flex: compact ? '0 0 140px' : '1 1 280px',
          height: 8, borderRadius: 999, overflow: 'hidden',
          display: 'flex', background: 'var(--pie-surface-2)',
          minWidth: 100,
        }}
        aria-hidden
      >
        {order.map((b) => {
          const v = VERDICT_STYLES[b];
          const c = buckets[b] || 0;
          const pct = (c / count) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={b}
              data-testid={`${testid}-${b}`}
              title={`${b.replace('_', ' ')}: ${c} of ${count}`}
              style={{ width: `${pct}%`, background: v.color, opacity: 0.7 }}
            />
          );
        })}
      </div>
      {!compact && (
        <div style={{ fontSize: 11, color: 'var(--pie-text-muted)' }} className="pie-mono">
          {order.map((b) => `${(buckets[b] || 0)} ${b.replace('_', ' ')}`).join(' · ')}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <section
      className="pie-card"
      style={{ textAlign: 'center', padding: '40px 24px' }}
      data-testid="pie-calibration-empty"
    >
      <Scale size={28} style={{ color: 'var(--pie-accent)', marginBottom: 12 }} aria-hidden />
      <h2 className="pie-display-md" style={{ marginBottom: 8 }}>No validated commitments yet.</h2>
      <p style={{ fontSize: 14, color: 'var(--pie-text-secondary)', maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
        Calibration review fills in once users have validated some commitments via the
        Validate page. The first row will appear here as soon as there&apos;s real
        predicted-vs-realised data to compare.
      </p>
    </section>
  );
}
